import React, { useEffect, useState } from 'react';
import { ChevronDown, Sparkles, Zap, Globe, Youtube } from 'lucide-react';
import { Button } from './ui/button';

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{
        backgroundImage: 'url(/contemplative-charles.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-[#0B1D2A]/85 to-black/90" />
      
      {/* Animated particles overlay */}
      <div className="absolute inset-0 opacity-30">
        <div className="stars" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div
          className={`transform transition-all duration-1000 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}
        >
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-8 backdrop-blur-sm">
            <Sparkles className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-[#D4AF37] text-sm font-medium" style={{ fontFamily: 'Lato, sans-serif' }}>
              Transformational Leader • AI Innovator • Global Educator
            </span>
          </div>

          {/* Main Heading */}
          <h1
            className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Bold Leadership for
            <br />
            <span className="text-[#D4AF37] glow-text">Creating a World That Works for Everyone</span>
          </h1>

          {/* Name */}
          <p
            className="text-2xl sm:text-3xl text-white/90 mb-6 font-semibold"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Charles Crawshaw
          </p>

          {/* Tagline with Emphasis */}
          <p
            className="text-lg sm:text-xl text-white/80 mb-8 max-w-3xl mx-auto leading-relaxed"
            style={{ fontFamily: 'Lato, sans-serif' }}
          >
            A transformational leader, AI innovator, and global educator committed to causing{' '}
            <span className="text-[#D4AF37] font-bold text-xl">World Peace</span> in our lifetime.
          </p>

          {/* Key Focus Areas */}
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            <div className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-lg backdrop-blur-sm">
              <Zap className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-white font-semibold text-base" style={{ fontFamily: 'Lato, sans-serif' }}>
                AI + Human Consciousness
              </span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-lg backdrop-blur-sm">
              <Globe className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-white font-semibold text-base" style={{ fontFamily: 'Lato, sans-serif' }}>
                World Peace Now Movement
              </span>
            </div>
          </div>

          <p
            className="text-base sm:text-lg text-white/70 mb-12 max-w-3xl mx-auto leading-relaxed"
            style={{ fontFamily: 'Lato, sans-serif' }}
          >
            With over 30 years of experience in personal development, leadership, and digital transformation,
            I create platforms, movements, and technologies that empower humanity to evolve.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              onClick={() => scrollToSection('movement')}
              className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-black font-bold px-8 py-6 text-lg rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-[#D4AF37]/50"
              style={{ fontFamily: 'Lato, sans-serif' }}
            >
              Join The Movement
            </Button>
            <Button
              onClick={() => window.open('https://www.youtube.com/@worldpeacenow888', '_blank', 'noopener,noreferrer')}
              className="bg-[#FF0000] hover:bg-[#FF0000]/90 text-white font-bold px-8 py-6 text-lg rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-[#FF0000]/50 flex items-center gap-2"
              style={{ fontFamily: 'Lato, sans-serif' }}
            >
              <Youtube className="w-6 h-6" />
              Watch on YouTube
            </Button>
            <Button
              onClick={() => scrollToSection('projects')}
              variant="outline"
              className="border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black font-semibold px-8 py-6 text-lg rounded-lg transition-all duration-300 hover:scale-105"
              style={{ fontFamily: 'Lato, sans-serif' }}
            >
              Explore AI & Transformation
            </Button>
          </div>
        </div>

        {/* Scroll Indicator */}
        <button
          onClick={() => scrollToSection('about')}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white/60 hover:text-[#D4AF37] transition-all duration-300 animate-bounce"
          aria-label="Scroll to About section"
        >
          <ChevronDown size={32} />
        </button>
      </div>
    </section>
  );
};

export default Hero;
